// /* global chrome */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
// Select components removed - not currently used
import { Search as Magnifier, Eye, Archive, Trash2, Loader2, Plus, X, Globe, BarChart3, ListOrdered, AlertTriangle, MapPin } from 'lucide-react';
import SparkleIcon from '../components/ui/SparkleIcon';
import LocationAutocomplete from '../components/ui/LocationAutocomplete';
import SearchTermAutocomplete from '../components/ui/SearchTermAutocomplete';
import { useLanguage } from '../contexts/LanguageContext';
import { postQuery, getQueries, updateQuery } from '../lib/QueryDataService';
import { useNavigate } from 'react-router-dom';
import ConfirmDialog from '../components/ui/ConfirmDialog';
// import ExtensionRecommendationModal from '../components/ui/ExtensionRecommendationModal';
import { ExtensionNoticeModal } from '../components/ui/ExtensionSubmit';

// Check if Chrome extension is available
const checkChromeExtension = () => {
  try {
    if (typeof window.chrome === 'undefined' || !window.chrome.runtime || !window.chrome.runtime.sendMessage) {
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

// SearchModal component - separated to prevent re-creation
const SearchModal = ({ 
  showSearchModal, 
  setShowSearchModal, 
  searchTerm, 
  location,
  searchType, 
  searchTypes,
  loading, 
  t, 
  isRTL, 
  handleSearch, 
  handleSearchTermChange,
  handleLocationChange,
  handleSearchTypeChange,
  setShowExtensionModal
}) => {
  const [activeTab, setActiveTab] = React.useState(() => {
    return localStorage.getItem('searchModalTab') || 'google';
  });
  const [googleQuery, setGoogleQuery] = React.useState('');

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('searchModalTab', tab);
  };

  const handleGoogleSearch = (e) => {
    e.preventDefault();
    if (!googleQuery.trim()) return;
    
    // Check if Chrome extension is installed
    if (!checkChromeExtension()) {
      setShowExtensionModal(true);
      return;
    }
    
    // Open Google search in a new tab
    const googleUrl = `https://www.google.com/search?q=${encodeURIComponent(googleQuery.trim())}`;
    window.open(googleUrl, '_blank');
    
    // Close modal and clear query
    setGoogleQuery('');
    setShowSearchModal(false);
  };

  if (!showSearchModal) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={() => setShowSearchModal(false)}
      />
      
      {/* Modal */}
      <Card className="relative w-full max-w-md mx-4 shadow-lg">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSearchModal(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-8 mt-3 border-b border-gray-200">
            <button
              type="button"
              onClick={() => handleTabChange('google')}
              className={`flex items-center gap-2 pb-3 text-base font-semibold transition-all duration-200 border-b-2 -mb-px ${
                activeTab === 'google'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Globe className="h-5 w-5" />
              {t('googleSearch')}
            </button>
            <button
              type="button"
              onClick={() => handleTabChange('business')}
              className={`flex items-center gap-2 pb-3 text-base font-semibold transition-all duration-200 border-b-2 -mb-px ${
                activeTab === 'business'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <SparkleIcon className="h-5 w-5" />
              {t('businessSearch')}
            </button>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          {activeTab === 'business' ? (
            <form onSubmit={handleSearch} className="space-y-5">
              {/* What Field */}
              <div className="space-y-2">
                <label htmlFor="modalSearchTerm" className="text-base font-semibold flex items-center gap-2">
                  <Magnifier className="h-5 w-5" />
                  {t('whatLabel')}
                </label>
                <SearchTermAutocomplete
                  value={searchTerm}
                  onChange={handleSearchTermChange}
                  placeholder={t('whatPlaceholder')}
                  isRTL={isRTL}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  required
                />
              </div>
              
              {/* Where Field */}
              <div className="space-y-2">
                <label htmlFor="modalLocation" className="text-base font-semibold flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  {t('whereLabel')}
                </label>
                <LocationAutocomplete
                  value={location}
                  onChange={handleLocationChange}
                  placeholder={t('wherePlaceholder')}
                  isRTL={isRTL}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  required
                />
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-x-2 w-full text-white focus:ring-2 focus:ring-primary border-0 shadow-md animated-gradient"
              >
                {loading ? t('loading') : t('search')}
                <Magnifier className="w-6 h-6 text-white self-center" />
              </Button>
            </form>
          ) : (
            <form onSubmit={handleGoogleSearch} className="space-y-5">
              {/* Google Search Field */}
              <div className="space-y-2">
                <label htmlFor="googleQuery" className="text-base font-semibold flex items-center gap-2">
                  <Magnifier className="h-5 w-5" />
                  {t('searchQuery')}
                </label>
                <SearchTermAutocomplete
                  value={googleQuery}
                  onChange={setGoogleQuery}
                  placeholder={t('googleSearchPlaceholder')}
                  isRTL={isRTL}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  required
                />
              </div>
              
              <Button
                type="submit"
                className="inline-flex items-center gap-x-2 w-full text-white focus:ring-2 focus:ring-primary border-0 shadow-md animated-gradient"
              >
                {t('searchOnGoogle')}
                <Globe className="w-5 h-5 text-white self-center" />
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SmartSearch = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  const [searchType, setSearchType] = useState('1');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(1);
  const [selectedQueryStatus, setSelectedQueryStatus] = useState(3);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, queryId: null, searchTerm: '' });
  const [updating, setUpdating] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [showProgressPopup, setShowProgressPopup] = useState(false);
  const [progressStep, setProgressStep] = useState(1);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  // const [showExtensionRecommendationModal, setShowExtensionRecommendationModal] = useState(false);
  const [showExtensionModal, setShowExtensionModal] = useState(false);
  const navigate = useNavigate();

  // Check if Chrome extension is available
  // const checkChromeExtension = () => {
  //   if (typeof chrome === 'undefined' || !chrome.runtime || !chrome.runtime.sendMessage) {
  //     return false;
  //   }
  //   return true;
  // };

  // Handle opening search modal with extension check
  const handleOpenSearchModal = () => {
    setShowSearchModal(true);
    // Disabled: Extension recommendation modal
    // if (!checkChromeExtension()) {
    //   setShowExtensionRecommendationModal(true);
    // }
  };

  // Handle install extension button click
  // const handleInstallExtension = () => {
  //   setShowExtensionRecommendationModal(false);
  //   setShowExtensionModal(true);
  // };

  // Mock search types
  const searchTypes = [
    { value: 1, label: t('contact') },
    { value: 2, label: t('submission') },
    { value: 3, label: t('lead') },
    { value: 4, label: t('opportunity') },
  ];

  // queryStatusLabels removed - not currently used

  // Get search type label by value
  const getSearchTypeLabel = (searchTypeValue) => {
    const searchType = searchTypes.find(type => type.value === searchTypeValue);
    return searchType ? searchType.label : t('unknown');
  };

  // Fetch queries by status and queryStatus
  const fetchQueries = async (status = selectedStatus, queryStatus = selectedQueryStatus, showLoader = true) => {
    try {
      setLoading(true);
      
      const response = await getQueries(status, queryStatus, showLoader);
      
      // The API returns { queries: [...], totalCount: number }
      const queries = response?.queries || response?.Queries || [];
      setResults(Array.isArray(queries) ? queries : []);
    } catch (error) {
      setResults([]);
      console.error('Fetch queries error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim() || !location.trim() || !searchType) return;
    
    // Show progress popup
    setShowProgressPopup(true);
    setProgressStep(1);
    setLoading(true);
    setHasSearched(true);
    
    // Update filters to active and ready for new search
    setSelectedStatus(1);
    setSelectedQueryStatus(3);
    
    try {
      let apiCompleted = false;
      let queryResponse = null;
      
      // Start API call and progress animation in parallel
      const apiPromise = (async () => {
        queryResponse = await postQuery({ searchTerm, location, searchType: Number(searchType), showLoader: false });
        await fetchQueries(1, 3, false);
        apiCompleted = true;
      })();

      (async () => {
        // Step 1: Researching relevant sites (4 seconds)
        await new Promise(resolve => setTimeout(resolve, 4000));
        if (apiCompleted) return;
        
        // Step 2: Analyzing relevance (4 seconds)
        setProgressStep(2);
        await new Promise(resolve => setTimeout(resolve, 4000));
        if (apiCompleted) return;
        
        // Step 3: Prioritizing (4 seconds)
        setProgressStep(3);
        await new Promise(resolve => setTimeout(resolve, 4000));
        if (apiCompleted) return;
        
        // Step 4: Compiling (4 seconds)
        setProgressStep(4);
        await new Promise(resolve => setTimeout(resolve, 4000));
      })();

      // Wait for API call to complete (progress animation will stop early if API is done)
      await apiPromise;
      
      // Navigate to results page after successful search (same as clicking "Open" button)
      if (queryResponse && queryResponse.id) {
        console.log('Navigating to results page with queryId:', queryResponse.id);
        console.log('Full queryResponse:', queryResponse);
        const navigationUrl = `/query/${queryResponse.id}?searchTerm=${encodeURIComponent(searchTerm)}&location=${encodeURIComponent(location)}`;
        console.log('Navigation URL:', navigationUrl);
        
        // Add a small delay to ensure navigation works properly
        setTimeout(() => {
          console.log('Executing navigation to:', navigationUrl);
          navigate(navigationUrl);
          setShowSearchModal(false); // Close modal after navigation
          // Clear search fields
          setSearchTerm('');
          setLocation('');
        }, 100);
      } else {
        console.log('No queryId found in response:', queryResponse);
        setShowSearchModal(false); // Close modal even if no queryId
      }
    } catch (error) {
      setResults([]);
      console.error('Search error:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      
      // Check if it's a 400 error and show the translated client message
      const is400Error = 
        (error.response && error.response.status === 400) ||
        (error.message && error.message.includes('400')) ||
        (error.status === 400);
      
      if (is400Error) {
        console.log('Detected 400 error, showing modal');
        setErrorMessage(t('dailySearchQuotaReached'));
        setShowErrorModal(true);
      } else {
        console.log('Not a 400 error, error details:', { 
          hasResponse: !!error.response, 
          status: error.response?.status, 
          message: error.message 
        });
      }
    } finally {
      setShowProgressPopup(false); // Hide progress popup
      setLoading(false);
    }
  };

  // handlePrefill and handleSubmit functions removed - not currently used

  const handleSearchTermChange = (value) => {
    setSearchTerm(value);
    setHasSearched(false);
  };

  const handleLocationChange = (value) => {
    setLocation(value);
    setHasSearched(false);
  };

  const handleSearchTypeChange = (value) => {
    setSearchType(value);
    setHasSearched(false);
  };

  // Handle main status change (Active, Archived, Deleted)
  const handleStatusChange = (status) => {
    setSelectedStatus(status);
    fetchQueries(status, selectedQueryStatus);
  };

  // Handle query status change (commented out - filters hidden)
  // const handleQueryStatusChange = (queryStatus) => {
  //   setSelectedQueryStatus(queryStatus);
  //   fetchQueries(selectedStatus, queryStatus);
  // };

  // Handle archive/unarchive query
  const handleArchive = async (queryId) => {
    try {
      setUpdating(true);
      // If viewing archived items, unarchive (set to active), otherwise archive
      const newStatus = selectedStatus === 0 ? 1 : 0;
      await updateQuery(queryId, newStatus);
      await fetchQueries();
    } catch (error) {
      console.error('Error updating query archive status:', error);
    } finally {
      setUpdating(false);
    }
  };

  // Handle delete/undelete query
  const handleDelete = async (queryId, searchTerm) => {
    // If viewing deleted items, undelete directly (no confirmation needed)
    if (selectedStatus === 2) {
      try {
        setUpdating(true);
        await updateQuery(queryId, 1); // Set status to Active
        await fetchQueries();
      } catch (error) {
        console.error('Error undeleting query:', error);
      } finally {
        setUpdating(false);
      }
    } else {
      // For active/archived items, show confirmation before deleting
      setDeleteConfirm({
        isOpen: true,
        queryId,
        searchTerm: searchTerm || 'this query'
      });
    }
  };

  // Get current progress step details
  const getProgressStepDetails = (step) => {
    switch (step) {
      case 1:
        return {
          icon: <Globe className="h-8 w-8 text-blue-600 animate-pulse" />,
          text: t('researchingSites')
        };
      case 2:
        return {
          icon: <BarChart3 className="h-8 w-8 text-green-600 animate-pulse" />,
          text: t('analyzingRelevance')
        };
      case 3:
        return {
          icon: <ListOrdered className="h-8 w-8 text-orange-600 animate-pulse" />,
          text: t('prioritizing')
        };
      case 4:
        return {
          icon: <Loader2 className="h-8 w-8 text-purple-600 animate-spin" />,
          text: t('almostThere')
        };
      default:
        return {
          icon: <Globe className="h-8 w-8 text-blue-600 animate-pulse" />,
          text: t('researchingSites')
        };
    }
  };

  // Confirm delete query
  const handleConfirmDelete = async () => {
    if (!deleteConfirm.queryId) return;

    try {
      setUpdating(true);
      await updateQuery(deleteConfirm.queryId, 2); // Set status to Deleted
      await fetchQueries();
      setDeleteConfirm({ isOpen: false, queryId: null, searchTerm: '' });
    } catch (error) {
      console.error('Error deleting query:', error);
    } finally {
      setUpdating(false);
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setDeleteConfirm({ isOpen: false, queryId: null, searchTerm: '' });
  };

  // Fetch queries on mount
  useEffect(() => {
    const loadInitialData = async () => {
      await fetchQueries();
      setInitialLoadComplete(true);
    };
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show modal if no results on initial load only (Active/Ready filters)
  useEffect(() => {
    if (initialLoadComplete && !hasSearched && results.length === 0 && selectedStatus === 1 && selectedQueryStatus === 3) {
      setShowSearchModal(true);
    }
  }, [initialLoadComplete, hasSearched, results.length, selectedStatus, selectedQueryStatus]);

  // Main status filter navigation (Active, Archived, Deleted)
  const statusTabs = [
    { value: 1, label: t('active') },
    { value: 0, label: t('archived') },
    { value: 2, label: t('deleted') },
  ];

  // Query status filter navigation (commented out - filters hidden)
  // const queryStatusTabs = [
  //   { value: 2, label: t('processing') },
  //   { value: 3, label: t('ready') },
  // ];



  return (
    <div className="space-y-4 sm:space-y-6 px-2 sm:px-0">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
            <SparkleIcon className="w-7 h-7 sm:w-8 sm:h-8 text-primary" />
            {t('smartSearch')}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t('smartSearchSubtitle')}
          </p>
        </div>
        <Button
          onClick={handleOpenSearchModal}
          className="inline-flex items-center gap-x-2 text-white focus:ring-2 focus:ring-primary border-0 shadow-md animated-gradient self-start sm:self-center"
        >
          <Plus className="w-5 h-5" />
          {t('newSearch')}
        </Button>
      </div>

      {/* Prelaunch Search Limit Notice - Hidden */}
      {/* <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-orange-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-orange-900">
              {t('prelaunchLimit')}
            </h3>
            <p className="text-sm text-orange-700 mt-1">
              {t('prelaunchLimitDescription')}
            </p>
          </div>
        </div>
      </div> */}

      {/* Results Section - now full width */}
      <div>
        {/* Status filter navigation - always visible */}
        <div className="flex flex-col md:flex-row md:items-center md:gap-8 space-y-2 md:space-y-0 mb-4">
          {/* Main Status Filters */}
          <div className="flex gap-4">
            {statusTabs.map(tab => {
              const isActive = selectedStatus === tab.value;
              const isPrimaryTab = tab.value === 1; // Active tab is primary
              
              let buttonClasses = 'px-3 py-1 rounded-full border text-sm transition-colors ';
              
              if (isActive) {
                if (isPrimaryTab) {
                  // Active primary tab - most prominent
                  buttonClasses += 'bg-primary text-primary-foreground border-primary font-semibold shadow-md';
                } else {
                  // Active secondary tab - standard active styling
                  buttonClasses += 'bg-primary text-primary-foreground border-primary font-medium';
                }
              } else {
                if (isPrimaryTab) {
                  // Inactive primary tab - more prominent than other inactive tabs
                  buttonClasses += 'bg-primary/10 text-primary border-primary/30 font-medium hover:bg-primary/20';
                } else {
                  // Inactive secondary tabs - subtle
                  buttonClasses += 'bg-muted text-muted-foreground border-muted-foreground/20 font-normal hover:bg-primary/10';
                }
              }
              
              return (
              <button
                key={tab.value}
                  className={buttonClasses}
                onClick={() => handleStatusChange(tab.value)}
              >
                {tab.label}
              </button>
              );
            })}
          </div>
          
          {/* Query Status Filters - Hidden */}
          {/* 
          <div className="flex gap-4">
            {queryStatusTabs.map(tab => {
              const isActive = selectedQueryStatus === tab.value;
              const isPrimaryTab = tab.value === 3; // Ready tab is primary
              
              let buttonClasses = 'px-3 py-1 rounded-full border text-sm transition-colors ';
              
              if (isActive) {
                if (isPrimaryTab) {
                  // Active primary tab - most prominent
                  buttonClasses += 'bg-orange-500 text-white border-orange-500 font-semibold shadow-md';
                } else {
                  // Active secondary tab - standard active styling
                  buttonClasses += 'bg-orange-500 text-white border-orange-500 font-medium';
                }
              } else {
                if (isPrimaryTab) {
                  // Inactive primary tab - more prominent than other inactive tabs
                  buttonClasses += 'bg-orange-50 text-orange-600 border-orange-200 font-medium hover:bg-orange-100 hover:border-orange-300';
                } else {
                  // Inactive secondary tabs - subtle
                  buttonClasses += 'bg-muted text-muted-foreground border-muted-foreground/20 font-normal hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200';
                }
              }
              
              return (
              <button
                key={tab.value}
                  className={buttonClasses}
                onClick={() => handleQueryStatusChange(tab.value)}
              >
                {tab.label}
              </button>
              );
            })}
          </div>
          */}
        </div>
        
        {results.length === 0 && !loading && !hasSearched && (
          <Card>
            <CardContent className="flex items-center justify-center py-8 sm:py-12">
              <div className="text-center">
                <Magnifier className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-muted-foreground" />
                <h3 className="mt-4 text-base sm:text-lg font-semibold">{t('readyToDiscover')}</h3>
                <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                  {t('enterSearchTermToFind')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {results.length > 0 && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              {results.map((result) => (
                <Card key={result.queryId} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-3">
                    <div className="flex items-center gap-2">
                      <CardTitle className="text-sm sm:text-base line-clamp-2">
                        {result.searchTerm}
                      </CardTitle>
                      <span className="text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground border border-muted-foreground/20">
                        {getSearchTypeLabel(result.searchType)}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex flex-row gap-2 justify-between items-center">
                      {/* Secondary actions - Archive and Delete (hidden for processing queries) */}
                      <div className="flex gap-2">
                        {result.queryStatus !== 2 && (
                          <>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-muted-foreground hover:text-foreground" 
                              title={selectedStatus === 0 ? t('unarchive') : t('archive')}
                              onClick={() => handleArchive(result.queryId)}
                              disabled={updating}
                            >
                              <Archive className="mr-2 h-4 w-4" />
                              {selectedStatus === 0 ? t('unarchive') : t('archive')}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="text-muted-foreground hover:text-foreground" 
                              title={selectedStatus === 2 ? t('undelete') : t('delete')}
                              onClick={() => handleDelete(result.queryId, result.searchTerm)}
                              disabled={updating}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              {selectedStatus === 2 ? t('undelete') : t('delete')}
                            </Button>
                          </>
                        )}
                      </div>
                      
                      {/* Primary action - Open button (when ready) or Processing indicator - moved to last position */}
                      {result.queryStatus === 1 || result.queryStatus === 2 ? (
                        <Button variant="outline" size="default" disabled>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {result.queryStatus === 1 ? t('queued') : t('processing')}
                        </Button>
                      ) : (
                        <Button 
                          variant="outline" 
                          size="default" 
                          className="font-semibold shadow-md bg-gradient-to-r from-white to-purple-50 border-purple-500 text-purple-600 hover:from-purple-50 hover:to-purple-100 hover:shadow-lg hover:shadow-purple-200 hover:scale-105 transition-all duration-300 hover:border-purple-600" 
                          title={t('open')} 
                          onClick={() => navigate(`/query/${result.queryId}?searchTerm=${encodeURIComponent(result.searchTerm)}`)}
                        >
                          <Eye className="mr-2 h-5 w-5" />
                          {t('open')}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
        
        {hasSearched && searchTerm && results.length === 0 && !loading && (
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <Magnifier className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-4 text-lg font-semibold">{t('noResults')}</h3>
                <p className="mt-2 text-muted-foreground">
                  {t('tryAdjustingSearch')}
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Search Modal */}
      <SearchModal
        showSearchModal={showSearchModal}
        setShowSearchModal={setShowSearchModal}
        searchTerm={searchTerm}
        location={location}
        searchType={searchType}
        searchTypes={searchTypes}
        loading={loading}
        t={t}
        isRTL={isRTL}
        handleSearch={handleSearch}
        handleSearchTermChange={handleSearchTermChange}
        handleLocationChange={handleLocationChange}
        handleSearchTypeChange={handleSearchTypeChange}
        setShowExtensionModal={setShowExtensionModal}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title={t('delete')}
        message={t('deleteProfileConfirmation', { profileName: deleteConfirm.searchTerm })}
        confirmText={updating ? t('loading') : t('delete')}
        cancelText={t('cancel')}
        variant="destructive"
        disabled={updating}
      />

      {/* Search Progress Popup */}
      {showProgressPopup && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/60" />
          
          {/* Progress Modal */}
          <Card className="relative w-[480px] mx-4 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center space-y-6">
                {/* Step Icon and Text */}
                <div className="flex items-center justify-center gap-4">
                  {getProgressStepDetails(progressStep).icon}
                  <span className="text-xl font-medium">
                    {getProgressStepDetails(progressStep).text}
                  </span>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-500 ease-out ${
                      progressStep === 1 ? 'w-1/4' : 
                      progressStep === 2 ? 'w-2/4' : 
                      progressStep === 3 ? 'w-3/4' : 'w-full'
                    }`}
                  />
                </div>
                
                {/* Step Indicator */}
                <p className="text-base text-muted-foreground">
                  Step {progressStep} of 4
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50 transition-opacity"
            onClick={() => setShowErrorModal(false)}
          />
          
          {/* Modal */}
          <div className="relative w-full max-w-md mx-4 shadow-lg">
            <div className="bg-white rounded-lg">
              <div className="pb-3 flex-shrink-0 p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                    Search Error
                  </div>
                  <button
                    onClick={() => setShowErrorModal(false)}
                    className="h-6 w-6 p-0 bg-transparent border-none cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="space-y-4 px-6 pb-6">
                <p className="text-sm text-gray-600">
                  {errorMessage}
                </p>
                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowErrorModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  >
                    {t('ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Extension Recommendation Modal - DISABLED */}
      {/* <ExtensionRecommendationModal
        isOpen={showExtensionRecommendationModal}
        onClose={() => setShowExtensionRecommendationModal(false)}
        onInstallExtension={handleInstallExtension}
      /> */}

      {/* Extension Installation Modal */}
      <ExtensionNoticeModal
        isOpen={showExtensionModal}
        onClose={() => setShowExtensionModal(false)}
      />
    </div>
  );
};

export default SmartSearch; 